import apiService from './api.service';

// DTOs and Interfaces
export interface IdentifyUserRequest {
  applicationId: string;
  userId: string;
  anonymousId?: string;
  email?: string;
  name?: string;
  properties?: Record<string, any>;
}

export interface UpdateUserPropertiesRequest {
  applicationId: string;
  properties: Record<string, any>;
}

export interface UserProfile {
  id: string;
  tenant_id: string;
  application_id: string;
  user_id: string;
  anonymous_id?: string;
  email?: string;
  name?: string;
  properties: Record<string, any>;
  first_seen_at: string;
  last_seen_at: string;
  session_count: number;
  total_events: number;
  created_at: string;
  updated_at: string;
}

export interface EventQueryFilters {
  applicationId: string;
  startTime?: string;
  endTime?: string;
  eventName?: string;
  userId?: string;
  limit?: number;
  offset?: number;
}

export interface Event {
  id: string;
  tenant_id: string;
  application_id: string;
  session_id?: string;
  user_id?: string;
  anonymous_id?: string;
  event_name: string;
  event_type?: string;
  timestamp: string;
  properties?: Record<string, any>;
  page_url?: string;
  page_title?: string;
  created_at: string;
}

export interface EventBreakdown {
  value: string;
  count: number;
  uniqueUsers: number;
}

export interface TimeSeriesData {
  timestamp: string;
  count: number;
  uniqueUsers: number;
}

export interface Journey {
  userId: string;
  events: Array<{
    eventName: string;
    timestamp: string;
    properties?: Record<string, any>;
  }>;
}

export interface PathAnalysis {
  paths: Array<{
    sequence: string[];
    count: number;
    conversionRate: number;
  }>;
}

export interface CreateFunnelRequest {
  name: string;
  description?: string;
  applicationId: string;
  steps: Array<{
    stepOrder: number;
    eventName: string;
    eventFilters?: Record<string, any>;
  }>;
  timeWindowHours?: number;
}

export interface Funnel {
  id: string;
  tenant_id: string;
  application_id: string;
  name: string;
  description?: string;
  step_count: number;
  time_window_hours: number;
  steps: Array<{
    id: string;
    funnel_id: string;
    step_order: number;
    event_name: string;
    event_filters?: Record<string, any>;
  }>;
  created_at: string;
  updated_at: string;
}

export interface FunnelAnalysis {
  funnelId: string;
  funnelName: string;
  totalEntries: number;
  completions: number;
  overallConversionRate: number;
  avgTimeToConvert?: number;
  steps: Array<{
    stepOrder: number;
    eventName: string;
    userCount: number;
    conversionRate: number;
    dropOffRate: number;
    avgTimeToNextStep?: number;
  }>;
}

export interface CreateCohortRequest {
  name: string;
  description?: string;
  applicationId: string;
  criteria: {
    matchType: 'all' | 'any';
    conditions: Array<{
      type: 'event_performed' | 'event_not_performed' | 'user_property';
      eventName?: string;
      property?: string;
      operator?: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
      value?: any;
      timeframe?: 'last_7_days' | 'last_30_days' | 'last_90_days' | 'all_time';
    }>;
  };
}

export interface Cohort {
  id: string;
  tenant_id: string;
  application_id: string;
  name: string;
  description?: string;
  criteria: any;
  user_count: number;
  last_calculated_at?: string;
  created_at: string;
  updated_at: string;
}

export interface RetentionMatrix {
  cohortId: string;
  cohortName: string;
  groupBy: 'day' | 'week' | 'month';
  cohorts: Array<{
    cohortDate: string;
    totalUsers: number;
    retentionByPeriod: Record<number, number>;
  }>;
}

class ProductAnalyticsService {
  private readonly baseUrl = '/v1/product-analytics';

  // ========== User Identification ==========

  async identifyUser(data: IdentifyUserRequest): Promise<UserProfile> {
    const response = await apiService.post<{ success: boolean; data: UserProfile }>(
      `${this.baseUrl}/users/identify`,
      data
    );
    return response.data;
  }

  async updateUserProperties(
    userId: string,
    data: UpdateUserPropertiesRequest
  ): Promise<{ success: boolean; message: string }> {
    return await apiService.put(`${this.baseUrl}/users/${userId}/properties`, data);
  }

  async getUserProfile(userId: string, applicationId: string): Promise<UserProfile> {
    const response = await apiService.get<{ success: boolean; data: UserProfile }>(
      `${this.baseUrl}/users/${userId}`,
      { params: { applicationId } }
    );
    return response.data;
  }

  // ========== Event Queries ==========

  async getEventsSummary(
    filters: { applicationId: string; startTime?: string; endTime?: string; eventName?: string }
  ): Promise<Array<{ name: string; type: string; count: number; uniqueUsers: number; lastSeen: string }>> {
    const response = await apiService.get<{
      success: boolean;
      data: Array<{ name: string; type: string; count: number; uniqueUsers: number; lastSeen: string }>;
    }>(`${this.baseUrl}/events/summary`, { params: filters });
    return response.data;
  }

  async getEventPropertyKeys(
    applicationId: string,
    eventName?: string
  ): Promise<string[]> {
    const params: any = { applicationId };
    if (eventName) params.eventName = eventName;
    const response = await apiService.get<{ success: boolean; data: string[] }>(
      `${this.baseUrl}/events/property-keys`,
      { params }
    );
    return response.data;
  }

  async queryEvents(
    filters: EventQueryFilters
  ): Promise<{ events: Event[]; total: number }> {
    const response = await apiService.get<{
      success: boolean;
      data: { events: Event[]; total: number };
    }>(`${this.baseUrl}/events`, { params: filters });
    return response.data;
  }

  async getEventBreakdown(
    eventName: string,
    applicationId: string,
    groupBy: string,
    startTime?: string,
    endTime?: string
  ): Promise<EventBreakdown[]> {
    const response = await apiService.get<{ success: boolean; data: EventBreakdown[] }>(
      `${this.baseUrl}/events/${encodeURIComponent(eventName)}/breakdown`,
      { params: { applicationId, groupBy, startTime, endTime } }
    );
    return response.data;
  }

  async getEventTimeseries(
    eventName: string,
    applicationId: string,
    interval: 'hour' | 'day' | 'week' | 'month',
    startTime?: string,
    endTime?: string
  ): Promise<TimeSeriesData[]> {
    const response = await apiService.get<{ success: boolean; data: TimeSeriesData[] }>(
      `${this.baseUrl}/events/${encodeURIComponent(eventName)}/timeseries`,
      { params: { applicationId, interval, startTime, endTime } }
    );
    return response.data;
  }

  async getAggregatedTimeseries(
    applicationId: string,
    interval: 'hour' | 'day' | 'week' | 'month',
    startTime?: string,
    endTime?: string
  ): Promise<TimeSeriesData[]> {
    const response = await apiService.get<{ success: boolean; data: TimeSeriesData[] }>(
      `${this.baseUrl}/events/timeseries`,
      { params: { applicationId, interval, startTime, endTime } }
    );
    return response.data;
  }

  // ========== User Journeys ==========

  async getUserJourney(
    userId: string,
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Journey> {
    const response = await apiService.get<{ success: boolean; data: Journey }>(
      `${this.baseUrl}/users/${userId}/journey`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getCommonPaths(
    applicationId: string,
    minLength?: number,
    maxLength?: number,
    startTime?: string,
    endTime?: string
  ): Promise<PathAnalysis> {
    const response = await apiService.get<{ success: boolean; data: PathAnalysis }>(
      `${this.baseUrl}/journeys/paths`,
      { params: { applicationId, minLength, maxLength, startTime, endTime } }
    );
    return response.data;
  }

  // ========== Funnels ==========

  async createFunnel(data: CreateFunnelRequest): Promise<Funnel> {
    const response = await apiService.post<{ success: boolean; data: Funnel }>(
      `${this.baseUrl}/funnels`,
      data
    );
    return response.data;
  }

  async listFunnels(applicationId: string): Promise<Funnel[]> {
    const response = await apiService.get<{ success: boolean; data: Funnel[] }>(
      `${this.baseUrl}/funnels`,
      { params: { applicationId } }
    );
    return response.data;
  }

  async getFunnel(funnelId: string): Promise<Funnel> {
    const response = await apiService.get<{ success: boolean; data: Funnel }>(
      `${this.baseUrl}/funnels/${funnelId}`
    );
    return response.data;
  }

  async updateFunnel(funnelId: string, data: Partial<CreateFunnelRequest>): Promise<Funnel> {
    const response = await apiService.put<{ success: boolean; data: Funnel }>(
      `${this.baseUrl}/funnels/${funnelId}`,
      data
    );
    return response.data;
  }

  async deleteFunnel(funnelId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.delete(`${this.baseUrl}/funnels/${funnelId}`);
  }

  async analyzeFunnel(
    funnelId: string,
    startTime?: string,
    endTime?: string,
    cohortId?: string
  ): Promise<FunnelAnalysis> {
    const response = await apiService.get<{ success: boolean; data: FunnelAnalysis }>(
      `${this.baseUrl}/funnels/${funnelId}/analysis`,
      { params: { startTime, endTime, cohortId } }
    );
    return response.data;
  }

  async getFunnelTrend(
    funnelId: string,
    startTime: string,
    endTime: string,
    buckets?: number
  ): Promise<Array<{ date: string; totalEntries: number; completions: number; conversionRate: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ date: string; totalEntries: number; completions: number; conversionRate: number }> }>(
      `${this.baseUrl}/funnels/${funnelId}/trend`,
      { params: { startTime, endTime, buckets } }
    );
    return response.data;
  }

  async getFunnelStepUsers(
    funnelId: string,
    stepOrder: number,
    startTime?: string,
    endTime?: string,
    limit?: number,
    offset?: number
  ): Promise<{ users: Array<{ userId: string; completedStep: boolean; timestamp?: string }>; total: number }> {
    const response = await apiService.get<{ success: boolean; data: { users: Array<{ userId: string; completedStep: boolean; timestamp?: string }>; total: number } }>(
      `${this.baseUrl}/funnels/${funnelId}/steps/${stepOrder}/users`,
      { params: { startTime, endTime, limit, offset } }
    );
    return response.data;
  }

  // ========== Cohorts ==========

  async createCohort(data: CreateCohortRequest): Promise<Cohort> {
    const response = await apiService.post<{ success: boolean; data: Cohort }>(
      `${this.baseUrl}/cohorts`,
      data
    );
    return response.data;
  }

  async listCohorts(applicationId: string): Promise<Cohort[]> {
    const response = await apiService.get<{ success: boolean; data: Cohort[] }>(
      `${this.baseUrl}/cohorts`,
      { params: { applicationId } }
    );
    return response.data;
  }

  async getCohort(cohortId: string): Promise<Cohort> {
    const response = await apiService.get<{ success: boolean; data: Cohort }>(
      `${this.baseUrl}/cohorts/${cohortId}`
    );
    return response.data;
  }

  async updateCohort(cohortId: string, data: Partial<CreateCohortRequest>): Promise<Cohort> {
    const response = await apiService.put<{ success: boolean; data: Cohort }>(
      `${this.baseUrl}/cohorts/${cohortId}`,
      data
    );
    return response.data;
  }

  async deleteCohort(cohortId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.delete(`${this.baseUrl}/cohorts/${cohortId}`);
  }

  async calculateCohort(cohortId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post(`${this.baseUrl}/cohorts/${cohortId}/calculate`);
  }

  async getCohortUsers(cohortId: string, limit?: number, offset?: number): Promise<UserProfile[]> {
    const response = await apiService.get<{ success: boolean; data: UserProfile[] }>(
      `${this.baseUrl}/cohorts/${cohortId}/users`,
      { params: { limit, offset } }
    );
    return response.data;
  }

  async getRetentionAnalysis(
    cohortId: string,
    groupBy?: 'day' | 'week' | 'month',
    periods?: number
  ): Promise<RetentionMatrix> {
    const response = await apiService.get<{ success: boolean; data: RetentionMatrix }>(
      `${this.baseUrl}/cohorts/${cohortId}/retention`,
      { params: { groupBy, periods } }
    );
    return response.data;
  }

  // ========== Retention with Dynamic Filters ==========

  async analyzeRetentionWithFilters(
    applicationId: string,
    filters: any,
    groupBy: 'day' | 'week' | 'month' = 'week',
    periods: number = 12,
    startTime?: string,
    endTime?: string
  ): Promise<RetentionMatrix> {
    const response = await apiService.post<{ success: boolean; data: RetentionMatrix }>(
      `${this.baseUrl}/retention/analyze`,
      {
        applicationId,
        filters,
        groupBy,
        periods,
        startTime,
        endTime,
      }
    );
    return response.data;
  }

  async previewFilteredUsers(
    applicationId: string,
    filters: any
  ): Promise<{ userCount: number }> {
    const response = await apiService.post<{ success: boolean; data: { userCount: number } }>(
      `${this.baseUrl}/retention/preview`,
      {
        applicationId,
        filters,
      }
    );
    return response.data;
  }

  // ========== Geography ==========

  async getSessionsByCountry(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ country: string; sessions: number; uniqueUsers: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ country: string; sessions: number; uniqueUsers: number }> }>(
      `${this.baseUrl}/geo/countries`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getSessionsByCity(
    applicationId: string,
    startTime?: string,
    endTime?: string,
    country?: string
  ): Promise<Array<{ city: string; country: string; sessions: number; uniqueUsers: number; latitude: number | null; longitude: number | null }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ city: string; country: string; sessions: number; uniqueUsers: number }> }>(
      `${this.baseUrl}/geo/cities`,
      { params: { applicationId, startTime, endTime, country } }
    );
    return response.data;
  }

  // ========== Devices & Browsers ==========

  async getDeviceBreakdown(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ deviceType: string; sessions: number; uniqueUsers: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ deviceType: string; sessions: number; uniqueUsers: number }> }>(
      `${this.baseUrl}/devices/breakdown`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getBrowserBreakdown(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ browser: string; sessions: number; uniqueUsers: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ browser: string; sessions: number; uniqueUsers: number }> }>(
      `${this.baseUrl}/browsers/breakdown`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getOSBreakdown(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ os: string; sessions: number; uniqueUsers: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ os: string; sessions: number; uniqueUsers: number }> }>(
      `${this.baseUrl}/os/breakdown`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getScreenBreakdown(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ resolution: string; sessions: number; uniqueUsers: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ resolution: string; sessions: number; uniqueUsers: number }> }>(
      `${this.baseUrl}/screens/breakdown`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  // ========== Traffic Sources ==========

  async getTrafficSources(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ source: string; pageViews: number; sessions: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ source: string; pageViews: number; sessions: number }> }>(
      `${this.baseUrl}/traffic/sources`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  // ========== Page Analytics ==========

  async getTopPages(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ path: string; views: number; uniqueSessions: number; avgDuration: number; avgScrollDepth: number; avgLcp: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ path: string; views: number; uniqueSessions: number; avgDuration: number; avgScrollDepth: number; avgLcp: number }> }>(
      `${this.baseUrl}/pages/top`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getEntryExitPages(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<{ entryPages: Array<{ path: string; entries: number }>; exitPages: Array<{ path: string; exits: number }>; avgPagesPerSession: number }> {
    const response = await apiService.get<{ success: boolean; data: { entryPages: Array<{ path: string; entries: number }>; exitPages: Array<{ path: string; exits: number }>; avgPagesPerSession: number } }>(
      `${this.baseUrl}/pages/entry-exit`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getPageFlow(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ fromPage: string; toPage: string; transitions: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ fromPage: string; toPage: string; transitions: number }> }>(
      `${this.baseUrl}/pages/flow`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  async getScrollDepthByPage(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ path: string; avgDepth: number; totalScrolls: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ path: string; avgDepth: number; totalScrolls: number }> }>(
      `${this.baseUrl}/pages/scroll-depth`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  // ========== Realtime ==========

  async getRealtimeMetrics(
    applicationId: string
  ): Promise<{ activeUsers: number; eventsPerMinute: number; topPages: Array<{ path: string; activeUsers: number }> }> {
    const response = await apiService.get<{ success: boolean; data: { activeUsers: number; eventsPerMinute: number; topPages: Array<{ path: string; activeUsers: number }> } }>(
      `${this.baseUrl}/realtime`,
      { params: { applicationId } }
    );
    return response.data;
  }

  // ========== User Timeline ==========

  async getUserTimeline(
    userId: string,
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<Array<{ type: string; timestamp: string; name: string; details?: any }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ type: string; timestamp: string; name: string; details?: any }> }>(
      `${this.baseUrl}/users/${encodeURIComponent(userId)}/timeline`,
      { params: { applicationId, startTime, endTime } }
    );
    return response.data;
  }

  // ========== Analytics Overview ==========

  async getAnalyticsOverview(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<{
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
    totalEvents: number;
    topEvents: Array<{ name: string; count: number }>;
  }> {
    const response = await apiService.get(
      `${this.baseUrl}/overview`,
      { params: { application_id: applicationId, start_time: startTime, end_time: endTime } }
    );
    return response.data || response;
  }
}

export default new ProductAnalyticsService();
